import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {Router} from '@angular/router';
import {AuthenticationService} from "./authentication.service";
import {isExternalSignInLandingPath} from "./chrome-free-routes";
import {lockoutRouteForSession} from "./gate-lockout-route";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private authenticationService: AuthenticationService, private router: Router) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(err => {
      // Status-code contract (mirrors the monolith middleware):
      //   401 = the session is rejected/dead — bad or expired token, or one revoked / logged-out elsewhere.
      //         The only sane response is to end the session and bounce to login.
      //   403 = the session is perfectly valid but was denied THIS specific action (role/scope/forms gate).
      //         Logging out here would be a category error, so we never do — the caller surfaces the error.
      //   404 = there is no such endpoint, and 405 = not for this verb. Neither says anything about the session,
      //         so both fall through untouched. Until #279 the monolith's gates ran in front of routing's
      //         terminal and answered BOTH cases 401, which landed here as a forced logout — the reason an SPA
      //         newer than the API signed people out, whether it had moved a route or only its method.
      // The 403s we give special UX to, all of the same shape: a session a GATE has restricted, hitting a
      // monolith endpoint that gate refuses. Route it to the one screen that can lift the restriction rather
      // than leaving the caller to show "action failed" over and over.
      //   1. A session owing its second factor (#282, AccountSecurityMiddleware → 403).
      //   2. A 'restricted' employee behind the required-forms gate (FormsGateMiddleware → 403).
      //   3. A 'restricted' employee behind the required-contact gate (ContactGateMiddleware → 403, #558).
      //   4. An employee whose employment has not started (EmploymentGateMiddleware → 403, #864). The screen it
      //      goes to cannot lift anything; it shows the start date and waits for GateRefreshService.
      // They are independent gates and an employee can carry more than one restricting claim, so WHICH screen
      // wins is not decided here: `lockoutRouteForSession` is the single rule the guard reads too. Were the
      // two to disagree, this interceptor would send such a session to one screen and the guard would bounce
      // it to another — with two gates that was a pointless extra hop and a flash of the wrong screen; with
      // three it is a loop.
      const lockout = err.status === 403 ? lockoutRouteForSession(this.authenticationService) : null;
      if (lockout) {
        // Fires on a remediation screen's OWN failures too — `E_AUTH_TWO_FACTOR_CODE_INCORRECT` is a 403 by
        // design (a 401 would be force-logged-out below before anything read the body). Navigating to the route
        // the browser is already on is a no-op and the screen still receives the error, so the overlap is
        // harmless. Do not "fix" it by narrowing on the error code: that would couple this interceptor to a
        // code list it has no other reason to know, and the list would then have to be maintained here too.
        this.router.navigate(['/' + lockout]).then();
      } else if (err.status === 401 && this.authenticationService.userValue
        && !ErrorInterceptor.sessionIsBeingReplaced()) {
        this.handleDeadSession();
      }

      const error = err;
      return throwError(() => error);
    }))
  }

  /**
   * A 401 the two guards above did not already explain, on a session that is not mid-replacement.
   *
   * Ordinarily that means the session is genuinely dead and the only sane response is `logout()`. One case is
   * NOT that (#279 follow-up, the self-grant panel/slider): a time-boxed platform-owner self-grant can expire
   * and have its company-scoped session row swept away by the backend UNDER a token this browser is still
   * holding — the very next request then 401s even though the platform/base identity behind it is still good.
   * `AuthenticationService.recoverToChooserOrLogout` is the ONE place that recovers-or-logs-out (shared with the
   * proactive `ContextExpiryTimerService`, which calls it before the token even 401s) — this is the guaranteed
   * fallback for a proactive trigger that never fires (a backgrounded tab throttles `setTimeout`).
   *
   * Narrowed to exactly the sessions that could plausibly have a base identity to fall back to:
   *  - `extId` present — only an external-identity session goes through `/auth/context/*` at all; a plain
   *    password session hits `E_GRANT_REDEEM_NOT_SOCIAL_SESSION` on the recovery endpoint just as it would on
   *    every other context endpoint, so there is no point trying.
   *  - `!isCompanyless` — the recovered session itself IS the contextless platform/base one. A 401 reaching
   *    THIS branch while already companyless means recovery failed too (or the platform identity itself died),
   *    and there is nothing further to recover TO — looping back into `recoverBaseSession()` here would either
   *    spin or (once the in-flight guard settles) still end at `logout()`, just one round trip later.
   */
  private handleDeadSession(): void {
    const user = this.authenticationService.userValue;
    if (user?.extId && !this.authenticationService.isCompanyless) {
      this.authenticationService.recoverToChooserOrLogout();
      return;
    }
    this.authenticationService.logout();
  }

  /**
   * True while the browser is sitting on the external sign-in handoff landing
   * (`/authentication/signing-in`) — the one page whose entire job is to REPLACE the session stored here.
   *
   * On that page a 401 does not mean "your session died", it means "the session in this browser has been
   * superseded and the new one has not been adopted yet": the provider callback that produced this URL already
   * rotated the `CM-Secret` cookie server-side, so any request still carrying the old bearer cannot decrypt and
   * 401s by design. Turning those into `logout()` is what made a fully successful redeem end at the login
   * screen (2026-08-13): three shell bootstrap calls 401'd at 14:08:06.871, ten milliseconds BEFORE
   * `/auth/social/complete` even started, and the resulting `logout()` wiped the session the exchange was about
   * to store. A fourth 401 landed after that store, and two `POST /auth/User/Logout` went out carrying two
   * DIFFERENT jwts (.928 and .930) — `logout()` reads `userValue` at call time, so a 401 racing an in-flight
   * sign-in can revoke a session other than the one it was raised for, including the one just minted.
   *
   * This narrows nothing else: the 401 still propagates to the caller, `EncryptionTokenMiddleware` still
   * refuses without a matching cookie, and `AuthGuard` is untouched. `SigningInComponent` owns the outcome of
   * this page and reports a genuine failure itself. Read from `location.pathname` rather than the router
   * because the requests this covers are fired during app bootstrap, before the first navigation event.
   */
  private static sessionIsBeingReplaced(): boolean {
    return isExternalSignInLandingPath(location.pathname);
  }
}
